import { useWatch } from 'react-hook-form';

import {
	PROGRAM_SECTIONS,
	programOwnerCheckoutAssignmentDtoSchema,
	type ProgramOwnerServiceConfig,
	SaleAssignmentMethod,
	saleAssignmentMethodValues,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import type { z } from 'zod';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateCheckoutAssignment } from '../../../../api/configuration/services/useUpdateCheckoutAssignment';
import { useServiceProgramInfo } from '../../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data for checkout assignment
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): z.infer<typeof programOwnerCheckoutAssignmentDtoSchema> {
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const checkoutConfig = programOwnerConfig?.checkout;
	const assignmentConfig = checkoutConfig?.assignment;

	return {
		dynamic: assignmentConfig?.dynamic ?? false,
		servicesProviders: assignmentConfig?.servicesProviders ?? {},
		[Services.SALE]: assignmentConfig?.[Services.SALE],
	};
}

export function CheckoutAssignmentForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateCheckoutAssignment = useUpdateCheckoutAssignment();
	const programInfo = useServiceProgramInfo(
		PROGRAM_SECTIONS.CHECKOUT,
		SUBSECTIONS.CHECKOUT_ASSIGNMENT,
	);

	// Early return if subsection is not part of the program or is Ekho-only
	if (!programInfo.shouldRender) {
		return null;
	}

	const handleSubmit = async (
		data: z.infer<typeof programOwnerCheckoutAssignmentDtoSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateCheckoutAssignment.mutateAsync(data);
			return { isSuccess: true };
		} catch (error) {
			console.error('Failed to update checkout assignment:', error);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.PROGRAM,
				section: PROGRAM_SECTIONS.CHECKOUT,
				subsection: SUBSECTIONS.CHECKOUT_ASSIGNMENT,
			}}
			form={{
				schema: programOwnerCheckoutAssignmentDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Assignment'
			description='Controls which dealership is selected as the merchant of record for a sale. Relevant for Ekho Access deals and Omni deals.'
			formTitle='Assignment rules'
			toggleSettings={{
				fieldName: 'dynamic',
				visibility: 'write',
			}}>
			<CheckoutAssignmentFormFields />
		</FormSection>
	);
}

/**
 * Component that renders the form fields and watches dynamic for conditional fields
 */
function CheckoutAssignmentFormFields() {
	const isDynamic = useWatch({ name: 'dynamic' });
	const primaryMethod = useWatch({ name: `${Services.SALE}.primaryMethod` });
	const isFixed = primaryMethod === SaleAssignmentMethod.FIXED;

	// Create dropdown options from enum values
	const assignmentMethodOptions = saleAssignmentMethodValues.map((value) => ({
		id: value,
		label: value
			.split('_')
			.map((word) => word.charAt(0) + word.slice(1).toLowerCase())
			.join(' '),
	}));

	return (
		<>
			{isDynamic && (
				<>
					<div style={{ display: 'flex', gap: '16px' }}>
						<Form.Field.Dropdown
							schemaField={`${Services.SALE}.primaryMethod`}
							labelText='Primary Method'
							descriptionText='The primary method for assigning sales during checkout.'
							required
							options={assignmentMethodOptions}
							placeholder='Select primary method'
							buttonVariant='outline'
						/>
						<Form.Field.Dropdown
							schemaField={`${Services.SALE}.fallbackMethod`}
							labelText='Fallback Method'
							descriptionText='The fallback method used when the primary method cannot assign a sale.'
							required
							options={assignmentMethodOptions}
							placeholder='Select fallback method'
							buttonVariant='outline'
						/>
					</div>
					{isFixed && (
						<Form.Field.Text
							schemaField={`${Services.SALE}.fixedSellingServicesProviderId`}
							labelText='Fixed Selling Services Provider ID'
							descriptionText='The services provider ID to use when primary method is FIXED.'
							required
							size='large'
							placeholder='Enter services provider ID'
						/>
					)}
					{/* TODO: limitedStates requires a new form field component for state-by-state configuration */}
				</>
			)}
		</>
	);
}

export default CheckoutAssignmentForm;
